import { useCurrentScene } from "@/modes/mode-data-context";
import { useCached3DObjectIfReady } from "@/object-cache";
import { selectActiveCadModel } from "@/store/cad/cad-slice";
import {
  addAnalysis,
  ReferencePlaneType,
  setActiveAnalysisId,
  setAnalysisCreationMode,
  setLastCreatedAnalysisId,
  closeAnalysisPanel,
  setAnalysisPanelTab,
  requestAnalysisRun,
  setActiveAnalysisVisibility,
  setShowActiveAnalysis,
  type AnalysisPanelTab,
} from "@/store/point-cloud-analysis-tool-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { activateTool, deactivateTool, ToolName } from "@/store/ui/ui-slice";
import {
  setObjectVisibility,
  ViewObjectTypes,
} from "@/store/view-options/view-options-slice";
import {
  ANALYSIS_SEARCH_DISTANCE_DEFAULT,
  pointCloudAnalysisColormapPresets,
  areColormapsSame,
  isColormapPresetName,
} from "@/utils/colormap-analysis-utils";
import { Dropdown, FaroButton, FaroIconButton, grey } from "@faro-lotv/flat-ui";
import { generateGUID } from "@faro-lotv/foundation";
import { isIElementGenericPointCloudStream } from "@faro-lotv/ielement-types";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import { Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState, type JSX } from "react";
import { cadSourceRevision } from "@/tools/cad-analysis/cad-source-revision";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { selectIElementWorldMatrix4 } from "@/utils/transform-conversion-parsed";
import { selectCanReadCAD } from "@/store/subscription-features/subscription-features-selectors";
import { selectClippingBox } from "@/store/clipping-box-selectors";
import {
  DEFAULT_CAD_COLOURING,
  type CadDeviationSettings,
} from "@/tools/cad-analysis/cad-deviation-colouring";
import { AnalysisDockingPanel, AnalysisPanelTabs } from "./analysis-panel";
import {
  CadDeviationControls,
  colormapOptions,
} from "@/components/r3f/renderers/analyses/colormap-options-forms";
import { CadDeviationScale } from "@/components/r3f/renderers/analyses/colormap-scale";
import { DistanceInputField } from "../distance-input-field";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";

type AnalysisDraftSettings = CadDeviationSettings & { clashClearance: number };

/** @returns The shared analysis entry panel; drawing begins only on the polygon command. */
export function AnalysisCreationPanel({
  embedded = false,
}: {
  embedded?: boolean;
}): JSX.Element {
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorHandlers();
  const { main } = useCurrentScene();
  const pointCloud = useCached3DObjectIfReady(
    main && isIElementGenericPointCloudStream(main) ? main : undefined,
    "AnalysisCreationCloud",
  );
  const cadElement = useAppSelector(selectActiveCadModel);
  const cadPose = useAppSelector(selectIElementWorldMatrix4(cadElement?.id));
  const cloudPose = useAppSelector(selectIElementWorldMatrix4(main?.id));
  const cad = useCached3DObjectIfReady(cadElement, "AnalysisCreationCad");
  const selected = useAppSelector((state) => state.cad.selectedSubtree);
  const drawing =
    useAppSelector((state) => state.pointCloudAnalysisTool.creationMode) ===
    "polygon";
  const mode =
    useAppSelector((state) => state.pointCloudAnalysisTool.panelTab) ?? "cad";
  const [starting, setStarting] = useState(false);
  const canReadCad = useAppSelector(selectCanReadCAD);
  const clipBox = useAppSelector(selectClippingBox);
  const generation = useRef(0);
  useEffect(() => {
    setStarting(false);
    generation.current += 1;
    return () => {
      generation.current += 1;
    };
  }, [pointCloud, cad, selected, mode, clipBox, cadElement]);
  const canCompare =
    canReadCad &&
    !!pointCloud &&
    !(pointCloud instanceof Error) &&
    !!cad &&
    !(cad instanceof Error);
  useEffect(() => {
    function cancel(event: KeyboardEvent): void {
      if (event.key === "Escape" && !drawing) {
        generation.current++;
        dispatch(closeAnalysisPanel());
        dispatch(deactivateTool());
      }
    }
    window.addEventListener("keydown", cancel);
    return () => window.removeEventListener("keydown", cancel);
  }, [drawing, dispatch]);
  async function compare(settings: AnalysisDraftSettings): Promise<void> {
    if (starting || drawing) return;
    if (mode === "polygon") {
      dispatch(setActiveAnalysisVisibility({ visible: false }));
      dispatch(setActiveAnalysisId(undefined));
      dispatch(setAnalysisCreationMode("polygon"));
      dispatch(activateTool(ToolName.analysis));
      return;
    }
    if (!canCompare || !cadElement) {
      return;
    }
    setStarting(true);
    pointCloud.updateWorldMatrix(true, false);
    cad.updateWorldMatrix(true, false);
    const cadWorld = cadPose.clone().multiply(cad.matrix);
    const cloudWorld = cloudPose.clone().multiply(pointCloud.matrix);
    const bounds = cad.boundingBox().clone().applyMatrix4(cadWorld);
    const id = generateGUID();
    const current = ++generation.current;
    const scope =
      selected?.cadId === cadElement.id &&
      (selected.all || selected.objectIds.length > 0)
        ? selected
        : {
            cadId: cadElement.id,
            objectId: 0,
            objectIds: [],
            all: true,
            name: cadElement.name,
          };
    let revision: string;
    try {
      revision = await cadSourceRevision(cadElement);
    } finally {
      if (current === generation.current) {
        setStarting(false);
      }
    }
    if (current !== generation.current) return;
    dispatch(setActiveAnalysisVisibility({ visible: false }));
    dispatch(
      addAnalysis({
        pointCloudID: pointCloud.iElement.id,
        analysis: {
          id,
          kind: "cad",
          referencePlaneType: ReferencePlaneType.cad,
          parentId: pointCloud.iElement.id,
          ...settings,
          labels: [],
          isDirty: true,
          isVisible: true,
          bounds: [bounds.min.toArray(), bounds.max.toArray()],
          cadReference: {
            ...scope,
            clipBox: clipBox ? structuredClone(clipBox) : undefined,
            revision,
            cadTransform: cadWorld.toArray(),
            cloudTransform: cloudWorld.toArray(),
          },
        },
      }),
    );
    dispatch(
      requestAnalysisRun({ analysisId: id, kind: mode, newlyCreated: true }),
    );
    dispatch(
      setObjectVisibility({ type: ViewObjectTypes.analyses, visibility: true }),
    );
    dispatch(setActiveAnalysisId(id));
    dispatch(setAnalysisPanelTab(mode));
    dispatch(setShowActiveAnalysis(true));
    dispatch(setLastCreatedAnalysisId(id));
    dispatch(deactivateTool());
  }
  return (
    <AnalysisCreationPanelContent
      mode={mode}
      embedded={embedded}
      busy={starting || drawing}
      modelName={cadElement?.name}
      subtreeName={
        selected?.cadId === cadElement?.id ? selected?.name : undefined
      }
      canCompare={
        mode === "polygon"
          ? !!pointCloud && !(pointCloud instanceof Error)
          : canCompare
      }
      clipped={!!clipBox}
      onChoose={(value) => {
        generation.current++;
        setStarting(false);
        dispatch(setAnalysisPanelTab(value));
      }}
      onClose={() => {
        generation.current++;
        dispatch(closeAnalysisPanel());
        dispatch(deactivateTool());
      }}
      onCompare={(settings) => {
        void compare(settings).catch((error: unknown) =>
          handleErrorWithToast({
            title: "Could not start CAD comparison",
            error,
          }),
        );
      }}
    />
  );
}

/** @returns Presentation shared by the analysis entry panel and isolated layout checks. */
export function AnalysisCreationPanelContent({
  mode,
  modelName,
  subtreeName,
  canCompare,
  clipped = false,
  onChoose,
  onClose,
  onCompare,
  embedded = false,
  busy = false,
}: {
  mode?: AnalysisPanelTab | "choose";
  modelName?: string;
  subtreeName?: string;
  canCompare: boolean;
  clipped?: boolean;
  embedded?: boolean;
  busy?: boolean;
  onChoose(mode: AnalysisPanelTab): void;
  onClose(): void;
  onCompare(settings: AnalysisDraftSettings): void;
}): JSX.Element {
  const [settings, setSettings] = useState<AnalysisDraftSettings>({
    tolerance: 0.01,
    searchDistance: ANALYSIS_SEARCH_DISTANCE_DEFAULT,
    deviationColouring: { ...DEFAULT_CAD_COLOURING },
    colormap: pointCloudAnalysisColormapPresets.rainbow,
    clashClearance: 0,
  });
  const [valid, setValid] = useState(true);
  const [clearanceValid, setClearanceValid] = useState(true);
  const distanceUnit = useDistanceUnit();
  const cadTab = mode === "cad" || mode === "choose" || mode === undefined;
  const scope = subtreeName
    ? "Scope: Selected subtree"
    : "Scope: Entire active model";
  const progress = mode === "polygon" ? "Drawing polygon" : "Starting analysis";
  const start = (
    <FaroButton
      disabled={
        !canCompare ||
        busy ||
        (cadTab && !valid) ||
        (mode === "clashes" && !clearanceValid)
      }
      onClick={() => onCompare(settings)}
    >
      <PlayArrowIcon fontSize="small" />
      {busy ? progress : "Start Analysis"}
    </FaroButton>
  );
  return (
    <AnalysisDockingPanel embedded={embedded} fillHeight={mode === "clashes"}>
      <Stack
        role="group"
        aria-label="Point cloud analysis creation"
        sx={{
          color: grey[0],
          gap: 2,
          minWidth: 0,
        }}
      >
        {!embedded && (
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              POINT CLOUD ANALYSIS
            </Typography>
            <FaroIconButton
              size="s"
              color={grey[0]}
              aria-label="Close analysis creation"
              onClick={onClose}
            >
              <CloseIcon fontSize="small" />
            </FaroIconButton>
          </Stack>
        )}
        {!embedded && (
          <AnalysisPanelTabs
            value={mode === "choose" || mode === undefined ? "cad" : mode}
            onChange={onChoose}
          />
        )}
        <Stack
          direction="row"
          useFlexGap
          sx={{
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "center",
            minWidth: 0,
          }}
        >
          <Stack
            spacing={1}
            sx={{ width: cadTab ? "190px" : "100%", maxWidth: "100%" }}
          >
            {mode !== "polygon" && (
              <>
                <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                  {modelName ?? "No CAD model selected"}
                </Typography>
                <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                  {subtreeName ?? "Entire active model"}
                </Typography>
                <Typography variant="caption">
                  {clipped ? "Scope: Current clip box" : scope}
                </Typography>
              </>
            )}
            {cadTab && (
              <CadDeviationControls
                flushOnBlur
                analysis={settings}
                onValidityChange={setValid}
                onChange={(next) =>
                  setSettings((previous) => ({ ...previous, ...next }))
                }
                palette={
                  <Dropdown
                    dark
                    variant="underlined"
                    options={colormapOptions}
                    value={
                      Object.entries(pointCloudAnalysisColormapPresets).find(
                        ([, colors]) =>
                          areColormapsSame(colors, settings.colormap),
                      )?.[0]
                    }
                    onChange={(event) => {
                      const name = event.target.value;
                      if (isColormapPresetName(name)) {
                        setSettings((previous) => ({
                          ...previous,
                          colormap: pointCloudAnalysisColormapPresets[name],
                        }));
                      }
                    }}
                  />
                }
              />
            )}
            {mode === "clashes" && (
              <DistanceInputField
                flushOnBlur
                dark
                fullWidth
                label="Required clearance"
                distanceUnit={distanceUnit}
                distanceInMeter={settings.clashClearance}
                onDistanceChanged={(clearance) => {
                  const validClearance =
                    clearance !== undefined &&
                    Number.isFinite(clearance) &&
                    clearance >= 0;
                  setClearanceValid(validClearance);
                  if (validClearance) {
                    setSettings((previous) => ({
                      ...previous,
                      clashClearance: clearance,
                    }));
                  }
                }}
              />
            )}
            {start}
          </Stack>
          {cadTab && (
            <CadDeviationScale
              analysis={settings}
              dark
              disabled={busy}
              onToleranceChange={(tolerance) =>
                setSettings((previous) => ({ ...previous, tolerance }))
              }
              onBandsChange={(tolerance, outerBandLimit) =>
                setSettings((previous) => ({
                  ...previous,
                  tolerance,
                  deviationColouring: {
                    ...DEFAULT_CAD_COLOURING,
                    ...previous.deviationColouring,
                    mode: "bands",
                    outerBandLimit,
                  },
                }))
              }
            />
          )}
        </Stack>
      </Stack>
    </AnalysisDockingPanel>
  );
}
